<template>
  <div class="artikli-popis forma-erp">
    <v-card outlined>
      <v-card-title class="naslov-kartice">
        {{ $store.state.documentActivLoc }}
        <span class="znak">{{ $store.state.companyName }} · {{ totalItems }} {{ mnozinaArtikala }}</span>
        <v-spacer></v-spacer>
        <span class="znak">{{ lang.ItemRowOpens }}</span>
      </v-card-title>

      <div class="alatke">
        <!-- Trazi se na potvrdu: kod 5.000 artikala i pretrage po svim poljima
             svaka tipka bi bila jedan upit na bazu. -->
        <div style="max-width:300px">
          <v-text-field
            v-model="searchName"
            :label="langC.Search"
            append-icon="mdi-magnify"
            clearable
            hide-details
            dense
            outlined
            @keyup.enter="doSearch"
            @click:append="doSearch"
            @click:clear="clearSearch"
          ></v-text-field>
        </div>
        <v-btn small color="primary" @click="doSearch">{{ langC.Search }}</v-btn>
        <div style="max-width:220px">
          <v-select
            v-model="grupaId"
            :items="grupeZaIzbor"
            item-text="GroupName"
            item-value="id"
            :label="lang.GroupName"
            hide-details
            dense
            outlined
            @change="doSearch"
          ></v-select>
        </div>
        <v-btn small :outlined="!viseStupaca" color="primary" :dark="viseStupaca" @click="viseStupaca = !viseStupaca">
          {{ viseStupaca ? lang.ItemLessColumns : lang.ItemMoreColumns }}
        </v-btn>
        <v-progress-circular v-if="saving" indeterminate color="primary" size="22" class="ml-2"></v-progress-circular>
        <v-spacer></v-spacer>
      </div>

      <v-data-table
        dense
        :headers="headers"
        :items="items"
        :options.sync="opcije"
        :server-items-length="totalItems"
        :loading="saving"
        disable-pagination
        hide-default-footer
        item-key="id"
        @click:row="navigateTo"
        :mobile-breakpoint="0"
        :item-class="redakKlasa"
      >
        <template v-slot:[`item.slika`]="{ item }">
          <img v-if="slikePrikaz[item.id]" :src="slikePrikaz[item.id]" class="slicica" alt="">
          <v-icon v-if="!slikePrikaz[item.id]" small color="grey lighten-3">mdi-image-outline</v-icon>
        </template>

        <!-- Sifra ide ispod naziva; naziv se krati na dva retka jer ide i do
             194 znaka, pa je redak znao biti visok pet linija. -->
        <template v-slot:[`item.itemName`]="{ item }">
          <div class="naziv" :title="item.itemName">{{ item.itemName }}</div>
          <div class="sitno">
            {{ item.itemID }}
            <span v-if="item.itemService" class="znak-usluga ml-1">{{ lang.ItemIsService }}</span>
            <span v-if="item.itemNotActiv" class="znak-neaktivan ml-1">{{ lang.ItemIsNotActiv }}</span>
          </div>
        </template>

        <template v-slot:[`item.GroupName`]="{ item }">
          <span v-if="item.GroupName">{{ item.GroupName }}</span>
          <span v-if="!item.GroupName" class="prazno">—</span>
        </template>
        <template v-slot:[`item.PotpunaSifra`]="{ item }">
          <span v-if="item.PotpunaSifra" class="sitno">{{ item.PotpunaSifra }}</span>
          <span v-if="!item.PotpunaSifra" class="prazno">—</span>
        </template>
        <template v-slot:[`item.itemUnit`]="{ item }">
          <span v-if="item.itemUnit">{{ item.itemUnit }}</span>
          <span v-if="!item.itemUnit" class="prazno">—</span>
        </template>

        <!-- Nula na cijeni znaci "nije upisano", pa se ne pise kao iznos. -->
        <template v-slot:[`item.itemPrice`]="{ item }">
          <span v-if="pNum(item.itemPrice) !== 0">{{ localMoney(item.itemPrice) }}</span>
          <span v-if="pNum(item.itemPrice) === 0" class="prazno">—</span>
        </template>
        <template v-slot:[`item.itemPriceWithVAT`]="{ item }">
          <span v-if="pNum(item.itemPriceWithVAT) !== 0">{{ localMoney(item.itemPriceWithVAT) }}</span>
          <span v-if="pNum(item.itemPriceWithVAT) === 0" class="prazno">—</span>
        </template>
        <!-- Sidrena cijena: 0,00 je stvarna cijena, a neutvrdjena se mora
             razlikovati od nje - zato crtica samo kad je polje prazno (NULL). -->
        <template v-slot:[`item.sidrenaCijena`]="{ item }">
          <span v-if="imaSidrenu(item)">{{ localMoney(item.sidrenaCijena) }}</span>
          <span v-else class="prazno" title="Sidrena cijena nije utvrđena — artikl ne smije u objavljeni cjenik">—</span>
        </template>
        <!-- PDV 0 % je stvarna stopa, ne prazno polje. -->
        <template v-slot:[`item.itemVAT1Percentage`]="{ item }">
          <span>{{ pNum(item.itemVAT1Percentage) }}</span>
        </template>
        <template v-slot:[`item.id`]="{ item }">
          <span class="sitno">{{ item.id }}</span>
        </template>

        <template v-slot:no-data>
          {{ lang.noData }}
        </template>
      </v-data-table>

      <div class="podnozje">
        <div class="stavka">
          <span>{{ lang.ItemsTotal }}</span>
          <b>{{ totalItems }}</b>
        </div>
        <div class="stavka">
          <span>{{ lang.ItemService }}</span>
          <b>{{ zbroj.usluga }}</b>
        </div>
        <div class="stavka">
          <span>{{ lang.ItemsNoPrice }}</span>
          <b>{{ zbroj.bezCijene }}</b>
        </div>
        <v-spacer></v-spacer>
        <v-pagination
          v-model="page"
          :length="totalPages"
          total-visible="7"
          next-icon="mdi-menu-right"
          prev-icon="mdi-menu-left"
          @input="handlePageChange"
        ></v-pagination>
        <div style="max-width:130px" class="ml-4">
          <v-select
            v-model="pageSize"
            :items="pageSizes"
            :label="lang.ItemsPerPage"
            hide-details
            dense
            outlined
            @change="handlePageSizeChange"
          ></v-select>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import ItemService from '@/services/ItemService'
import GroupService from '@/services/GroupService'
import langEn from './itemDescEn'
import langHr from './itemDescHR'
import commEn from "@/language/commonEN"
import commHr from "@/language/commonHR"
import moneyFormat from '@/snippets/moneyFormat'
import { dohvatiSliku } from './slikaArtikla'

export default {
  name: 'itemlist',
  data () {
    return {
      lang: {},
      langC: {},
      items: [],
      searchName: "",
      page: 1,
      totalPages: 0,
      pageSize: 30,
      pageSizes: [10, 30, 50],
      totalItems: 0,
      saving: false,
      viseStupaca: false,
      grupaId: null,
      grupe: [],
      slikePrikaz: {},
      zbroj: { usluga: 0, bezCijene: 0 },
      // Sortiranje se salje na server, da poreda cijeli sifrarnik.
      opcije: { sortBy: ['itemName'], sortDesc: [false] }
    }
  },
  mounted () {
      if (this.$store.state.language === "EN") {
        this.lang = langEn
        this.langC = commEn;
      }
      if (this.$store.state.language === "HR") {
        this.lang = langHr
        this.langC = commHr;
      }
      // Stupce slaze izracunato svojstvo "headers".
      this.$store.dispatch('seteditItem', 0)
      if (this.$store.state.searchState) {
        this.searchName = this.$store.state.searchState.searchName ? this.$store.state.searchState.searchName : null
        this.page = this.$store.state.searchState.page ? this.$store.state.searchState.page + 1 : 0
        this.pageSize = this.$store.state.searchState.pageSize ? this.$store.state.searchState.pageSize : 30
        this.$store.dispatch('setSearchState', null)
      }
      this.dohvatiGrupe()
      this.retrievePartners()
  },
  computed: {
    headers () {
      const stupci = []
      if (this.imaSlika) {
        stupci.push({ text: '', value: 'slika', sortable: false, width: '46px' })
      }
      stupci.push({ text: this.bezZvjezdice(this.lang.ItemName) + ' / ' + this.bezZvjezdice(this.lang.ItemID), value: 'itemName' })
      stupci.push({ text: this.lang.GroupName, value: 'GroupName', width: '130px' })
      if (this.viseStupaca) {
        stupci.push({ text: 'KPD', value: 'PotpunaSifra', width: '120px' })
      }
      stupci.push({ text: this.lang.ItemUnit, value: 'itemUnit', width: '70px' })
      stupci.push({ text: this.lang.ItemPrice, align: 'right', value: 'itemPrice', width: '110px' })
      stupci.push({ text: this.lang.ItemVAT1Percentage, align: 'right', value: 'itemVAT1Percentage', width: '80px' })
      stupci.push({ text: this.lang.ItemPriceWithVAT, align: 'right', value: 'itemPriceWithVAT', width: '120px' })
      // Sidrena cijena - NN 101/2026. Ide odmah uz cijenu s PDV-om, jer se te
      // dvije usporedjuju; sortiranje se iskljucuje da se prazne (neutvrdjene)
      // ne bi pomijesale s nulama.
      stupci.push({ text: 'Sidrena', align: 'right', value: 'sidrenaCijena', width: '110px', sortable: false })
      if (this.viseStupaca) {
        stupci.push({ text: 'Db id', align: 'right', value: 'id', width: '80px' })
      }
      return stupci
    },
    grupeZaIzbor () {
      return [{ id: null, GroupName: this.lang.ItemAllGroups }].concat(this.grupe)
    },
    imaSlika () {
      return this.items.some(artikl => this.slikePrikaz[artikl.id])
    },
    mnozinaArtikala () {
      const n = this.totalItems
      const zadnja = n % 10
      const zadnje2 = n % 100
      if (zadnja === 1 && zadnje2 !== 11) { return this.lang.ItemWord1 }
      if (zadnja >= 2 && zadnja <= 4 && (zadnje2 < 12 || zadnje2 > 14)) { return this.lang.ItemWord2 }
      return this.lang.ItemWord5
    }
  },
  watch: {
    // Klik na zaglavlje mijenja poredak - novi upit, jer se sortira cijeli popis.
    opcije: {
      deep: true,
      handler (novo, staro) {
        if (!staro) { return }
        const istoPolje = JSON.stringify(novo.sortBy) === JSON.stringify(staro.sortBy)
        const istiSmjer = JSON.stringify(novo.sortDesc) === JSON.stringify(staro.sortDesc)
        if (istoPolje && istiSmjer) { return }
        this.page = 1
        this.retrievePartners()
      }
    }
  },
  methods: {
      localMoney(moneyToFormat) {
        return moneyFormat(moneyToFormat)
      },
      pNum (vrijednost) {
        return parseFloat(vrijednost) || 0
      },
      // NULL znaci "nije utvrdjena", a 0,00 je stvarna cijena - zato se ne
      // smije provjeravati istinitost broja.
      imaSidrenu (artikl) {
        const v = artikl ? artikl.sidrenaCijena : null
        return v !== null && v !== undefined && v !== ''
      },
      // Nazivi u sifrarniku nose zvjezdicu za obavezno polje ("Sifra *"); u
      // zaglavlju popisa se nema sto unijeti, pa zvjezdica samo smeta.
      bezZvjezdice (naziv) {
        return String(naziv || '').replace(/\s*\*\s*$/, '')
      },
      // Grupe se dohvacaju za trenutnu firmu; "groupStore" zna nositi grupe
      // firme u kojoj se radilo prije, pa se na njega ne oslanjamo.
      async dohvatiGrupe () {
        try {
          const { data } = await GroupService.choose({ CompanyId: this.$store.state.companyid })
          this.grupe = data && data.groups ? data.groups : []
        } catch (greska) {
          this.grupe = []
        }
      },
      redakKlasa (artikl) {
        return artikl.itemNotActiv ? 'redak-neaktivan' : ''
      },
      // Uploadana slika trazi prijavu, pa se dohvaca s tokenom; javni link ide
      // izravno. Dohvaceno se pamti, pa listanje natrag ne vuce iste slike opet.
      async ucitajSlike () {
        const novi = {}
        for (const artikl of this.items) {
          if (artikl.ItemPicture1) {
            novi[artikl.id] = await dohvatiSliku(artikl.ItemPicture1, this.$store.state.companyid)
          }
        }
        this.slikePrikaz = novi
      },
      setQuery() {
        const query = {}
        query.CompanyId = this.$store.state.companyid
        query.searchName = this.searchName ? this.searchName.trim() : null
        query.page = this.page ? this.page - 1 : 0
        query.pageSize = this.pageSize ? this.pageSize : 30
        query.searchItem = null
        query.GroupId = this.grupaId ? this.grupaId : null
        query.sortBy = this.opcije.sortBy && this.opcije.sortBy.length ? this.opcije.sortBy[0] : 'itemName'
        query.sortDesc = this.opcije.sortDesc && this.opcije.sortDesc.length ? this.opcije.sortDesc[0] : false
        return query
      },
      async retrievePartners() {
        
        const query = this.setQuery()
        this.saving = true
        const {data} = await ItemService.indexlist(query)
        this.saving = false

        this.items = data && data.items ? data.items : []
        this.totalItems = data && data.totalPages ? data.totalPages : 0
        this.totalPages = parseInt(this.totalItems / this.pageSize) + 1
        this.zbroj = data && data.zbroj ? data.zbroj : { usluga: 0, bezCijene: 0 }
        this.ucitajSlike()
      },

      handlePageChange(value) {
        this.page = value;
        this.retrievePartners();
      },

      handlePageSizeChange(size) {
        this.pageSize = size;
        this.page = 1;
        this.retrievePartners();
      },

      doSearch() {
        this.page = 1
        this.retrievePartners()
      },
      clearSearch() {
        this.searchName = ''
        this.doSearch()
      },
      navigateTo (params) {
        this.$store.dispatch('setSearchState', this.setQuery())
        this.$store.dispatch('setItemid', params.id)
        this.$router.push({
          name: 'itemview'
        })
      }

  },
  components: {
  }
}
</script>

<style scoped>
.artikli-popis .naslov-kartice {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #1565c0;
  background: #eef4fb;
  border-bottom: 2px solid #1976d2;
  padding: 8px 16px;
  min-height: 0;
  gap: 10px;
}
.artikli-popis .naslov-kartice .znak {
  font-size: 13px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}
.artikli-popis .alatke {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

/* Naziv se krati na dva retka - ide i do 194 znaka, pa je redak znao biti
   visok pet linija i na stranicu je stalo desetak artikala umjesto trideset. */
.artikli-popis .naziv {
  font-weight: 500;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.artikli-popis .sitno {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.2;
}
.artikli-popis .prazno {
  color: #cfd8dc;
}
.artikli-popis .slicica {
  width: 34px;
  height: 34px;
  object-fit: cover;
  display: block;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.artikli-popis .znak-usluga {
  display: inline-block;
  font-size: 11px;
  background: #fff3e0;
  color: #e65100;
  border: 1px solid #ffcc80;
  border-radius: 9px;
  padding: 0 7px;
}
.artikli-popis .znak-neaktivan {
  display: inline-block;
  font-size: 11px;
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
  border-radius: 9px;
  padding: 0 7px;
}
.artikli-popis >>> tr.redak-neaktivan td {
  opacity: 0.55;
}
.artikli-popis >>> tbody tr {
  cursor: pointer;
}
.artikli-popis >>> td {
  white-space: nowrap;
}
.artikli-popis >>> td:nth-child(1),
.artikli-popis >>> td:nth-child(2) {
  white-space: normal;
}
.artikli-popis .podnozje {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  padding: 10px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  background: #fafafa;
}
.artikli-popis .podnozje .stavka span {
  display: block;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.1;
}
.artikli-popis .podnozje .stavka b {
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}
</style>
